import { useMemo } from "react";
import { Link, useRoute } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard } from "@/components/mnf/UI";
import { LEGAL_DOCS, legalDocBySlug } from "@/lib/legalDocs";

/**
 * /legal hub and /legal/:slug viewer.
 *
 * Long-form legal documents rendered from markdown shipped with the app.
 * Headings, lists, blockquotes, and tables are rendered with a tiny
 * markdown-to-React converter so the docs read clearly on a phone screen
 * without pulling in a heavy markdown library.
 */

export default function Legal() {
  const [, params] = useRoute("/legal/:slug");
  const slug = params?.slug ?? null;
  const doc = slug ? legalDocBySlug(slug) : null;

  if (slug && doc) {
    return <LegalViewer slug={slug} title={doc.title} body={doc.body} />;
  }

  return (
    <div className="pb-24">
      <ScreenHeader
        variant="cream"
        eyebrow="The fine print, told plainly"
        title="Our policies"
        subtitle="The agreements that hold this community together. Read what matters to you."
      />

      <div className="px-5 mt-2 space-y-2.5">
        {LEGAL_DOCS.map((d) => (
          <Link
            key={d.slug}
            href={`/legal/${d.slug}`}
            className="no-underline block"
            data-testid={`link-legal-${d.slug}`}
          >
            <GlassCard organic padding={16} style={{ cursor: "pointer" }}>
              <div className="flex items-center gap-3">
                <div
                  aria-hidden
                  className="shrink-0 inline-flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "14px 6px 14px 6px",
                    background: "rgba(30,74,53,0.10)",
                    color: "var(--mnf-f)",
                  }}
                >
                  <Icon name="description" size={22} weight={400} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-serif"
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: "var(--mnf-fd)",
                      lineHeight: 1.3,
                    }}
                  >
                    {d.title}
                  </p>
                  <p
                    className="mnf-body"
                    style={{
                      fontSize: 12,
                      color: "var(--mnf-tg)",
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {summarize(d.body)}
                  </p>
                </div>
                <Icon
                  name="chevron_right"
                  size={20}
                  weight={400}
                  color="var(--mnf-tg)"
                />
              </div>
            </GlassCard>
          </Link>
        ))}

        <p
          className="mnf-body italic text-center"
          style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 16 }}
        >
          Operated by Fit Sovereign LLC. Technology by Drese Co LLC.
        </p>
      </div>
    </div>
  );
}

function summarize(body: string): string {
  const lines = body.split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    if (
      line &&
      !line.startsWith("#") &&
      !line.startsWith(">") &&
      !line.startsWith("---") &&
      !line.startsWith("***") &&
      !line.startsWith("|") &&
      !line.startsWith("**Effective") &&
      !line.startsWith("**Last") &&
      line.length > 12
    ) {
      const stripped = line.replace(/[*_`]/g, "");
      return stripped.length > 110 ? stripped.slice(0, 107) + "..." : stripped;
    }
  }
  return "Tap to read.";
}

function LegalViewer({
  slug,
  title,
  body,
}: {
  slug: string;
  title: string;
  body: string;
}) {
  const blocks = useMemo(() => parseMarkdown(body), [body]);

  return (
    <div className="pb-24">
      <ScreenHeader variant="cream" eyebrow="Policy" title={title} />

      <div className="px-5 mt-2">
        <Link
          href="/legal"
          className="no-underline inline-flex items-center gap-1 mb-3"
          style={{ color: "var(--mnf-t)", fontSize: 13 }}
          data-testid="link-legal-back"
        >
          <Icon name="arrow_back" size={16} weight={400} color="var(--mnf-t)" />
          All policies
        </Link>

        <GlassCard
          organic
          padding={20}
          data-testid={`card-legal-${slug}`}
          style={{ background: "rgba(245,240,232,0.92)" }}
        >
          <div className="legal-prose">
            {blocks.map((b, i) => (
              <RenderBlock key={i} block={b} />
            ))}
          </div>
        </GlassCard>

        <p
          className="mnf-body italic text-center"
          style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 16 }}
        >
          Operated by Fit Sovereign LLC. Technology by Drese Co LLC.
        </p>
      </div>
    </div>
  );
}

/* =========================== Tiny markdown parser ========================== */

type Block =
  | { kind: "h1"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "quote"; text: string }
  | { kind: "hr" }
  | { kind: "table"; headers: string[]; rows: string[][] };

function parseMarkdown(src: string): Block[] {
  const lines = src.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  const flushParagraph = (buf: string[]) => {
    if (buf.length === 0) return;
    blocks.push({ kind: "p", text: buf.join(" ").trim() });
    buf.length = 0;
  };

  const paraBuf: string[] = [];

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      flushParagraph(paraBuf);
      i++;
      continue;
    }

    if (trimmed.startsWith("# ")) {
      flushParagraph(paraBuf);
      blocks.push({ kind: "h1", text: trimmed.slice(2).trim() });
      i++;
      continue;
    }
    if (trimmed.startsWith("## ")) {
      flushParagraph(paraBuf);
      blocks.push({ kind: "h2", text: trimmed.slice(3).trim() });
      i++;
      continue;
    }
    if (trimmed.startsWith("### ")) {
      flushParagraph(paraBuf);
      blocks.push({ kind: "h3", text: trimmed.slice(4).trim() });
      i++;
      continue;
    }
    if (trimmed === "---") {
      flushParagraph(paraBuf);
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }
    if (trimmed.startsWith("> ")) {
      flushParagraph(paraBuf);
      const buf: string[] = [trimmed.slice(2).trim()];
      i++;
      while (i < lines.length && lines[i].trim().startsWith("> ")) {
        buf.push(lines[i].trim().slice(2).trim());
        i++;
      }
      blocks.push({ kind: "quote", text: buf.join(" ") });
      continue;
    }
    if (/^[-*]\s/.test(trimmed)) {
      flushParagraph(paraBuf);
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      flushParagraph(paraBuf);
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      flushParagraph(paraBuf);
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const cells = lines[i]
          .trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((c) => c.trim());
        rows.push(cells);
        i++;
      }
      if (rows.length >= 2 && rows[1].every((c) => /^-+:?$|^:?-+:?$/.test(c))) {
        const headers = rows[0];
        const body = rows.slice(2);
        blocks.push({ kind: "table", headers, rows: body });
      } else if (rows.length >= 1) {
        blocks.push({ kind: "table", headers: rows[0], rows: rows.slice(1) });
      }
      continue;
    }

    paraBuf.push(trimmed);
    i++;
  }
  flushParagraph(paraBuf);
  return blocks;
}

function renderInline(text: string): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  // Handle [text](url), **bold**, *italic*
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  const pieces: (string | JSX.Element)[] = [];
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIndex) pieces.push(text.slice(lastIndex, m.index));
    const label = m[1];
    const url = m[2];
    if (url.startsWith("/")) {
      pieces.push(
        <Link
          key={`l${key++}`}
          href={url}
          style={{ color: "var(--mnf-t)", textDecoration: "underline" }}
        >
          {label}
        </Link>,
      );
    } else {
      pieces.push(
        <a
          key={`l${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--mnf-t)", textDecoration: "underline" }}
        >
          {label}
        </a>,
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) pieces.push(text.slice(lastIndex));

  // Now do bold within each string piece
  for (const piece of pieces) {
    if (typeof piece !== "string") {
      out.push(piece);
      continue;
    }
    const parts = piece.split(/(\*\*[^*]+\*\*)/g);
    for (const p of parts) {
      if (p.startsWith("**") && p.endsWith("**")) {
        out.push(
          <strong key={`b${key++}`} style={{ fontWeight: 600 }}>
            {p.slice(2, -2)}
          </strong>,
        );
      } else if (p) {
        out.push(p);
      }
    }
  }
  return out;
}

function RenderBlock({ block }: { block: Block }) {
  switch (block.kind) {
    case "h1":
      return (
        <h1
          className="mnf-serif"
          style={{
            fontSize: 22,
            fontWeight: 500,
            color: "var(--mnf-fd)",
            marginTop: 4,
            marginBottom: 12,
            lineHeight: 1.2,
          }}
        >
          {renderInline(block.text)}
        </h1>
      );
    case "h2":
      return (
        <h2
          className="mnf-serif"
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: "var(--mnf-fd)",
            marginTop: 20,
            marginBottom: 8,
            lineHeight: 1.25,
          }}
        >
          {renderInline(block.text)}
        </h2>
      );
    case "h3":
      return (
        <h3
          className="mnf-serif"
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: "var(--mnf-fd)",
            marginTop: 16,
            marginBottom: 6,
            lineHeight: 1.3,
          }}
        >
          {renderInline(block.text)}
        </h3>
      );
    case "p":
      return (
        <p
          className="mnf-body"
          style={{
            fontSize: 14,
            lineHeight: 1.65,
            color: "var(--mnf-body)",
            marginBottom: 10,
          }}
        >
          {renderInline(block.text)}
        </p>
      );
    case "ul":
      return (
        <ul style={{ marginBottom: 12, paddingLeft: 18 }}>
          {block.items.map((item, i) => (
            <li
              key={i}
              className="mnf-body"
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "var(--mnf-body)",
                marginBottom: 4,
                listStyleType: "disc",
              }}
            >
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol style={{ marginBottom: 12, paddingLeft: 22 }}>
          {block.items.map((item, i) => (
            <li
              key={i}
              className="mnf-body"
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "var(--mnf-body)",
                marginBottom: 4,
                listStyleType: "decimal",
              }}
            >
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote
          className="mnf-body"
          style={{
            margin: "12px 0",
            padding: "12px 14px",
            borderLeft: "3px solid var(--mnf-gold, #A07820)",
            background: "rgba(160,120,32,0.08)",
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "var(--mnf-body)",
            borderRadius: "0 8px 8px 0",
          }}
        >
          {renderInline(block.text)}
        </blockquote>
      );
    case "hr":
      return (
        <hr
          style={{
            border: 0,
            borderTop: "1px solid rgba(30,74,53,0.18)",
            margin: "20px 0",
          }}
        />
      );
    case "table":
      return (
        <div
          style={{
            overflowX: "auto",
            margin: "12px 0",
            borderRadius: 12,
            border: "1px solid rgba(30,74,53,0.14)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12.5,
            }}
          >
            <thead>
              <tr style={{ background: "rgba(30,74,53,0.08)" }}>
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    className="mnf-body"
                    style={{
                      padding: "8px 10px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "var(--mnf-fd)",
                      borderBottom: "1px solid rgba(30,74,53,0.18)",
                    }}
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="mnf-body"
                      style={{
                        padding: "8px 10px",
                        color: "var(--mnf-body)",
                        borderTop: "1px solid rgba(30,74,53,0.10)",
                        verticalAlign: "top",
                        lineHeight: 1.5,
                      }}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
